import type * as Three from 'three';

type ThreeModule = typeof Three;
type RoomEnvironmentCtor = new () => Three.Scene;

/*
 * Builds the architectural hero sculpture: translucent aubergine glass monoliths,
 * a copper crossbar echoing the logo, a copper ring, and connected copper nodes.
 * Returns a cleanup function that disposes every GPU resource.
 */
export function buildHeroScene(
  THREE: ThreeModule,
  RoomEnvironment: RoomEnvironmentCtor,
  container: HTMLElement,
  reduceMotion: boolean,
): () => void {
  let width = container.clientWidth;
  let height = container.clientHeight;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
  renderer.setSize(width, height);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.domElement.setAttribute('aria-hidden', 'true');
  renderer.domElement.style.display = 'block';
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environment = envTexture;

  const camera = new THREE.PerspectiveCamera(34, width / height, 0.1, 100);
  const setCameraDistance = () => {
    camera.position.set(0, 0.55, width < 520 ? 13.5 : 11.5);
    camera.lookAt(0, 0.35, 0);
  };
  setCameraDistance();

  scene.add(new THREE.HemisphereLight(0xf8f5f0, 0x301d32, 0.6));
  const key = new THREE.DirectionalLight(0xffe2cf, 2.4);
  key.position.set(4, 6, 5);
  scene.add(key);
  const rim = new THREE.PointLight(0xa65f46, 40, 18);
  rim.position.set(-4, -1, 3);
  scene.add(rim);
  const fill = new THREE.DirectionalLight(0xa9b5aa, 0.5);
  fill.position.set(-5, 2, -4);
  scene.add(fill);

  const glass = new THREE.MeshPhysicalMaterial({
    color: 0x4a2c4e,
    roughness: 0.12,
    metalness: 0.05,
    transparent: true,
    opacity: 0.66,
    clearcoat: 1,
    clearcoatRoughness: 0.08,
    envMapIntensity: 1.2,
  });
  const glassLight = glass.clone();
  glassLight.color = new THREE.Color(0x6b4d6e);
  glassLight.opacity = 0.42;
  const copper = new THREE.MeshStandardMaterial({ color: 0xa65f46, metalness: 1, roughness: 0.28, envMapIntensity: 1.3 });
  const edgeMaterial = new THREE.LineBasicMaterial({ color: 0xd49a7f, transparent: true, opacity: 0.55 });
  const connectorMaterial = new THREE.LineBasicMaterial({ color: 0xd49a7f, transparent: true, opacity: 0.3 });

  const group = new THREE.Group();
  scene.add(group);

  const addBox = (size: [number, number, number], position: [number, number, number], rotationY: number, material: Three.Material) => {
    const geometry = new THREE.BoxGeometry(...size);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(...position);
    mesh.rotation.y = rotationY;
    mesh.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry), edgeMaterial));
    group.add(mesh);
    return mesh;
  };

  addBox([1.1, 3.8, 1.1], [-1.35, 0.05, 0], 0.55, glass);
  addBox([0.85, 2.5, 0.85], [0.35, -0.6, 1.1], 0.2, glassLight);
  addBox([0.7, 4.6, 0.7], [1.55, 0.45, -0.9], 0.8, glass);
  addBox([4.6, 0.08, 3], [0, -1.9, 0], 0.35, glassLight);

  const bar = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.07, 0.07), copper);
  bar.position.set(0.1, 0.6, 0.35);
  bar.rotation.y = 0.35;
  group.add(bar);

  const ring = new THREE.Mesh(new THREE.TorusGeometry(1.25, 0.025, 24, 160), copper);
  ring.position.set(0.2, 2.2, 0);
  ring.rotation.x = 1.2;
  group.add(ring);

  const octaGeometry = new THREE.OctahedronGeometry(0.42);
  const octa = new THREE.Mesh(octaGeometry, glass);
  octa.position.set(-0.45, 2.75, 0.9);
  octa.add(new THREE.LineSegments(new THREE.EdgesGeometry(octaGeometry), edgeMaterial));
  group.add(octa);

  const nodePositions: [number, number, number][] = [
    [-2.6, 1.4, 0.6],
    [-1.9, 2.4, -0.6],
    [2.5, 1.9, 0.4],
    [2.8, -0.4, 1.1],
    [-2.4, -1.1, 1.3],
  ];
  const nodeGeometry = new THREE.SphereGeometry(0.07, 20, 20);
  const nodes = nodePositions.map((p) => {
    const node = new THREE.Mesh(nodeGeometry, copper);
    node.position.set(...p);
    group.add(node);
    return node;
  });
  const connectorPoints = [...nodePositions, nodePositions[0]].map((p) => new THREE.Vector3(...p));
  const connectorGeometry = new THREE.BufferGeometry().setFromPoints(connectorPoints);
  const connectors = new THREE.Line(connectorGeometry, connectorMaterial);
  group.add(connectors);

  const baseRotation = -0.35;
  group.rotation.y = baseRotation;

  const pointer = { x: 0, y: 0 };
  const eased = { x: 0, y: 0 };
  const onPointerMove = (event: PointerEvent) => {
    pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    pointer.y = (event.clientY / window.innerHeight) * 2 - 1;
  };

  const clock = new THREE.Clock();
  let frame = 0;
  let visible = true;

  const render = () => renderer.render(scene, camera);

  const tick = () => {
    frame = requestAnimationFrame(tick);
    if (!visible) return;
    const t = clock.getElapsedTime();
    eased.x += (pointer.x - eased.x) * 0.04;
    eased.y += (pointer.y - eased.y) * 0.04;
    group.rotation.y = baseRotation + Math.sin(t * 0.12) * 0.35 + eased.x * 0.22;
    group.rotation.x = eased.y * 0.07;
    octa.rotation.y = t * 0.4;
    octa.rotation.x = t * 0.2;
    octa.position.y = 2.75 + Math.sin(t * 0.9) * 0.12;
    ring.rotation.z = t * 0.12;
    nodes.forEach((node, i) => {
      node.position.y = nodePositions[i][1] + Math.sin(t * 0.8 + i) * 0.08;
    });
    const positions = connectorGeometry.attributes.position as Three.BufferAttribute;
    nodes.forEach((node, i) => positions.setY(i, node.position.y));
    positions.setY(nodes.length, nodes[0].position.y);
    positions.needsUpdate = true;
    render();
  };

  const resizeObserver = new ResizeObserver(() => {
    width = container.clientWidth;
    height = container.clientHeight;
    if (!width || !height) return;
    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    setCameraDistance();
    if (reduceMotion) render();
  });
  resizeObserver.observe(container);

  const intersectionObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting && document.visibilityState === 'visible';
  });
  intersectionObserver.observe(container);

  if (reduceMotion) {
    render();
  } else {
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    tick();
  }

  return () => {
    cancelAnimationFrame(frame);
    resizeObserver.disconnect();
    intersectionObserver.disconnect();
    window.removeEventListener('pointermove', onPointerMove);
    scene.traverse((object) => {
      const mesh = object as Three.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
    });
    [glass, glassLight, copper, edgeMaterial, connectorMaterial].forEach((m) => m.dispose());
    envTexture.dispose();
    pmrem.dispose();
    renderer.dispose();
    renderer.domElement.remove();
  };
}

export function supportsWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}
